/**
 * usStates (api side): the server mirror of src/lib/usStates.ts (a state
 * picker and a ZIP validator on client addresses). Kept in byte parity on
 * the code/name pairs (api/_shared/__tests__/usStates.test.ts reads the
 * src file's source text and compares); the api project cannot import
 * across to src/ (the routeClasses.test.ts boundary), so the list is
 * duplicated rather than shared.
 *
 * `stateCodeFromText` maps a legacy full state name or a lowercase code
 * to its code: the CareTree importers use it, and so does the
 * duplicate-address key (address.ts), whose client twin reads the same
 * function in src/lib/usStates.ts.
 */

export interface UsStateEntry {
  code: string;
  name: string;
}

/** The 50 states and DC, by name, in this order. */
export const US_STATES: readonly UsStateEntry[] = [
  { code: 'AL', name: 'Alabama' },
  { code: 'AK', name: 'Alaska' },
  { code: 'AZ', name: 'Arizona' },
  { code: 'AR', name: 'Arkansas' },
  { code: 'CA', name: 'California' },
  { code: 'CO', name: 'Colorado' },
  { code: 'CT', name: 'Connecticut' },
  { code: 'DE', name: 'Delaware' },
  { code: 'DC', name: 'District of Columbia' },
  { code: 'FL', name: 'Florida' },
  { code: 'GA', name: 'Georgia' },
  { code: 'HI', name: 'Hawaii' },
  { code: 'ID', name: 'Idaho' },
  { code: 'IL', name: 'Illinois' },
  { code: 'IN', name: 'Indiana' },
  { code: 'IA', name: 'Iowa' },
  { code: 'KS', name: 'Kansas' },
  { code: 'KY', name: 'Kentucky' },
  { code: 'LA', name: 'Louisiana' },
  { code: 'ME', name: 'Maine' },
  { code: 'MD', name: 'Maryland' },
  { code: 'MA', name: 'Massachusetts' },
  { code: 'MI', name: 'Michigan' },
  { code: 'MN', name: 'Minnesota' },
  { code: 'MS', name: 'Mississippi' },
  { code: 'MO', name: 'Missouri' },
  { code: 'MT', name: 'Montana' },
  { code: 'NE', name: 'Nebraska' },
  { code: 'NV', name: 'Nevada' },
  { code: 'NH', name: 'New Hampshire' },
  { code: 'NJ', name: 'New Jersey' },
  { code: 'NM', name: 'New Mexico' },
  { code: 'NY', name: 'New York' },
  { code: 'NC', name: 'North Carolina' },
  { code: 'ND', name: 'North Dakota' },
  { code: 'OH', name: 'Ohio' },
  { code: 'OK', name: 'Oklahoma' },
  { code: 'OR', name: 'Oregon' },
  { code: 'PA', name: 'Pennsylvania' },
  { code: 'RI', name: 'Rhode Island' },
  { code: 'SC', name: 'South Carolina' },
  { code: 'SD', name: 'South Dakota' },
  { code: 'TN', name: 'Tennessee' },
  { code: 'TX', name: 'Texas' },
  { code: 'UT', name: 'Utah' },
  { code: 'VT', name: 'Vermont' },
  { code: 'VA', name: 'Virginia' },
  { code: 'WA', name: 'Washington' },
  { code: 'WV', name: 'West Virginia' },
  { code: 'WI', name: 'Wisconsin' },
  { code: 'WY', name: 'Wyoming' },
];

/** The five inhabited territories, by name, in this order. */
export const US_TERRITORIES: readonly UsStateEntry[] = [
  { code: 'AS', name: 'American Samoa' },
  { code: 'GU', name: 'Guam' },
  { code: 'MP', name: 'Northern Mariana Islands' },
  { code: 'PR', name: 'Puerto Rico' },
  { code: 'VI', name: 'U.S. Virgin Islands' },
];

/** Every code across both lists, for a cheap membership check. */
export const US_STATE_CODES: ReadonlySet<string> = new Set(
  [...US_STATES, ...US_TERRITORIES].map((s) => s.code),
);

/** True only when `v` is exactly one of the 56 codes: uppercase, no
 *  surrounding space. */
export function isUsStateCode(v: string): boolean {
  return US_STATE_CODES.has(v);
}

const ZIP_RE = /^\d{5}(-?\d{4})?$/;

/** True when the trimmed value is a five-digit ZIP or a ZIP+4, hyphenated
 *  or as nine bare digits. */
export function isValidZip(v: string): boolean {
  return ZIP_RE.test(v.trim());
}

/** The trimmed value, with nine bare digits rewritten as "12345-6789".
 *  Any other value (a valid five-digit ZIP, an already-hyphenated ZIP+4,
 *  or an invalid value) is returned trimmed and otherwise unchanged. */
export function normalizeZip(v: string): string {
  const trimmed = v.trim();
  if (/^\d{9}$/.test(trimmed)) {
    return `${trimmed.slice(0, 5)}-${trimmed.slice(5)}`;
  }
  return trimmed;
}

const CODE_BY_UPPER_CODE = new Map<string, string>(
  [...US_STATES, ...US_TERRITORIES].map((s) => [s.code.toUpperCase(), s.code]),
);
const CODE_BY_UPPER_NAME = new Map<string, string>(
  [...US_STATES, ...US_TERRITORIES].map((s) => [s.name.toUpperCase(), s.code]),
);

/**
 * Map free text to a state code: the trimmed value if it is already a
 * code (any case); else the code whose name matches case-insensitively;
 * else null. Never guesses at an abbreviation ("N.J.") that is neither a
 * real code nor a real name.
 */
export function stateCodeFromText(v: string): string | null {
  const trimmed = v.trim();
  if (trimmed === '') return null;
  const byCode = CODE_BY_UPPER_CODE.get(trimmed.toUpperCase());
  if (byCode !== undefined) return byCode;
  const byName = CODE_BY_UPPER_NAME.get(trimmed.toUpperCase());
  if (byName !== undefined) return byName;
  return null;
}

/**
 * A typed state read as its code: `stateCodeFromText` of the trimmed
 * text, else of the text with every `.` removed, so a dotted
 * abbreviation reads as its code while a dotted name ("U.S. Virgin
 * Islands") still reads by name. The address check and the placement
 * rule read a typed state through this one function.
 */
export function typedStateCode(v: string | null | undefined): string | null {
  const trimmed = (v ?? '').trim();
  if (trimmed === '') return null;
  return stateCodeFromText(trimmed) ?? stateCodeFromText(trimmed.replace(/\./g, ''));
}
